import React, { useMemo } from 'react';
import { 
  Truck, 
  BookOpen, 
  ShoppingBag, 
  Users,
  Sparkles,
  HeartPulse,
  Phone,
  Mail,
  Globe
} from 'lucide-react';
import { useLanguage } from '../../context/LanguageContext';
import { useStore } from '../../context/StoreContext';

const TopBanner = ({ onSelectProduct, onSelectArticle }) => {
  const { lang, toggleLanguage, t } = useLanguage();
  const { settings, cmsContent, blogs = [], products = [] } = useStore();

  // Dynamic Ticker Items Generator
  const tickerItems = useMemo(() => {
    const items = [];
    const freeDeliveryLimit = settings?.freeDeliveryThreshold || 5000;
    const formattedLimit = Number(freeDeliveryLimit).toLocaleString();

    // 1. Custom Admin Announcement (if exists)
    let customTextUrdu = cmsContent?.announcement?.textUrdu?.trim();
    let customTextEn = cmsContent?.announcement?.textEn?.trim();

    if (customTextUrdu) {
      // Auto-correct any legacy 2,500 to 5,000
      customTextUrdu = customTextUrdu.replace(/2[,\s]?500/g, formattedLimit);
      if (customTextEn) {
        customTextEn = customTextEn.replace(/2[,\s]?500/g, formattedLimit);
      }

      items.push({
        id: 'custom-announcement',
        type: 'announcement',
        badgeUrdu: 'اہم اعلان',
        badgeEn: 'Notice',
        badgeColor: 'bg-amber-400 text-pansar-950',
        textUrdu: customTextUrdu,
        textEn: customTextEn || customTextUrdu,
        clickable: false
      });
    }

    // 2. Free Delivery Notice (dynamically from settings.freeDeliveryThreshold = 5,000)
    // Only display separate badge if not already announced above
    const isAlreadyMentioned = customTextUrdu && (customTextUrdu.includes('ڈلیوری') || customTextUrdu.includes('ڈیلیوری'));
    if (!isAlreadyMentioned) {
      items.push({
        id: 'free-delivery',
        type: 'offer',
        badgeUrdu: 'مفت ڈلیوری',
        badgeEn: 'Free Shipping',
        badgeColor: 'bg-emerald-400 text-emerald-950',
        textUrdu: `پورے پاکستان میں ${formattedLimit} روپے یا اس سے زائد خریداری پر مفت ڈلیوری!`,
        textEn: `Free Delivery Across Pakistan on Orders of Rs. ${formattedLimit} or Above!`,
        clickable: false
      });
    }

    // 3. Latest Health / Tibbi Articles from Blogs
    if (blogs && blogs.length > 0) {
      blogs.slice(0, 3).forEach((blog, idx) => {
        items.push({
          id: `blog-${blog.id || idx}`,
          type: 'article',
          badgeUrdu: 'تازہ طبی مضمون',
          badgeEn: 'Latest Article',
          badgeColor: 'bg-teal-300 text-teal-950',
          textUrdu: blog.titleUrdu || blog.title,
          textEn: blog.titleEn || blog.title || blog.titleUrdu,
          clickable: true,
          data: blog
        });
      });
    }

    // 4. Featured / Special Products (from actual store products)
    const validProds = (products || []).filter(p => Boolean(p && (p.nameUrdu || p.nameEn)));
    if (validProds.length > 0) {
      validProds.slice(0, 3).forEach((prod, idx) => {
        const displayPrice = prod.price || prod.variants?.[0]?.price || 0;
        const priceTextUr = displayPrice ? ` (قیمت: ₨ ${displayPrice.toLocaleString()})` : '';
        const priceTextEn = displayPrice ? ` (Rs. ${displayPrice.toLocaleString()})` : '';
        items.push({
          id: `prod-${prod.id || idx}`,
          type: 'product',
          badgeUrdu: 'خاص دوا',
          badgeEn: 'Featured',
          badgeColor: 'bg-yellow-400 text-yellow-950',
          textUrdu: `${prod.nameUrdu || prod.name} — خالص قدرتی و طبی نسخہ${priceTextUr}`,
          textEn: `${prod.nameEn || prod.name || prod.nameUrdu} — 100% Pure Botanical Formulation${priceTextEn}`,
          clickable: true,
          data: prod
        });
      });
    }

    // 5. Dynamic Recent Social Proof Orders (strictly from currently available products)
    if (validProds.length > 0) {
      const recentBuyersPool = [
        { nameUrdu: 'محمد عثمان', nameEn: 'M. Usman', cityUrdu: 'لاہور', cityEn: 'Lahore' },
        { nameUrdu: 'حاجی بشیر احمد', nameEn: 'Haji Basheer Ahmed', cityUrdu: 'راولپنڈی', cityEn: 'Rawalpindi' },
        { nameUrdu: 'ڈاکٹر فرہاد خان', nameEn: 'Dr. Farhad Khan', cityUrdu: 'پشاور', cityEn: 'Peshawar' },
        { nameUrdu: 'ملک عثمان غنی', nameEn: 'Malik Usman Ghani', cityUrdu: 'فیصل آباد', cityEn: 'Faisalabad' },
        { nameUrdu: 'سید عابد علی شاہ', nameEn: 'Syed Abid Ali Shah', cityUrdu: 'اسلام آباد', cityEn: 'Islamabad' },
        { nameUrdu: 'چوہدری نوید اختر', nameEn: 'Chaudhry Naveed', cityUrdu: 'گوجرانوالہ', cityEn: 'Gujranwala' },
        { nameUrdu: 'طارق محمود صدیقی', nameEn: 'Tariq Mehmood', cityUrdu: 'کراچی', cityEn: 'Karachi' },
        { nameUrdu: 'عائشہ صدیقہ', nameEn: 'Ayesha Siddiqa', cityUrdu: 'ملتان', cityEn: 'Multan' }
      ];

      validProds.slice(0, 4).forEach((prod, idx) => {
        const buyer = recentBuyersPool[idx % recentBuyersPool.length];
        const vWeight = prod.variants?.[0]?.weight || '';
        const vTextUr = vWeight ? ` (${vWeight})` : '';
        const vTextEn = prod.variants?.[0]?.weightEn ? ` (${prod.variants[0].weightEn})` : vTextUr;

        items.push({
          id: `social-buyer-${prod.id || idx}`,
          type: 'product',
          badgeUrdu: 'حالیہ خریدار',
          badgeEn: 'Recent Order',
          badgeColor: 'bg-cyan-300 text-cyan-950',
          textUrdu: `${buyer.nameUrdu} (${buyer.cityUrdu}) نے ابھی ${prod.nameUrdu || prod.name}${vTextUr} کا آرڈر بک کیا!`,
          textEn: `${buyer.nameEn} (${buyer.cityEn}) recently ordered ${prod.nameEn || prod.name || prod.nameUrdu}${vTextEn}!`,
          clickable: true,
          data: prod
        });
      });
    }

    // 6. Tibbi Guidance Tag
    items.push({
      id: 'tibbi-core',
      type: 'guidance',
      badgeUrdu: 'طبِ پاکستانی',
      badgeEn: 'Tibbi Guide',
      badgeColor: 'bg-emerald-300 text-emerald-950',
      textUrdu: 'قانون مفرد اعضاء از حضرت صابر ملتانیؒ — خالص قدرتی و نباتاتی علاج کا مرکز',
      textEn: 'Tibb-e-Pakistani (Qanoon-e-Mufrad Aza) — 100% Pure Botanical Remedies',
      clickable: false
    });

    return items;
  }, [cmsContent, blogs, products]);

  const handleItemClick = (item) => {
    if (!item) return;
    if (item.type === 'article' && onSelectArticle && item.data) {
      onSelectArticle(item.data);
    } else if (item.type === 'product' && onSelectProduct && item.data) {
      onSelectProduct(item.data);
    }
  };

  // Render a single ticker item element
  const renderItem = (item, idx) => {
    const text = lang === 'ur' ? item.textUrdu : (item.textEn || item.textUrdu);
    const badge = lang === 'ur' ? item.badgeUrdu : (item.badgeEn || item.badgeUrdu);

    return (
      <div 
        key={`${item.id}-${idx}`}
        onClick={() => handleItemClick(item)}
        className={`inline-flex items-center gap-2 sm:gap-2.5 px-3 sm:px-4 py-1.5 transition whitespace-nowrap select-none ${
          item.clickable 
            ? 'cursor-pointer hover:bg-pansar-800/90 rounded-xl group' 
            : 'cursor-default'
        }`}
        title={item.clickable ? (lang === 'ur' ? 'کھولنے کے لیے کلک کریں' : 'Click to open') : ''}
      >
        {/* Category Badge */}
        <span className={`px-2.5 py-0.5 rounded-full font-bold text-[10px] sm:text-[11px] font-subheading shrink-0 shadow-2xs tracking-wide ${item.badgeColor || 'bg-amber-400 text-pansar-950'}`}>
          {badge}
        </span>

        {/* Text */}
        <span className={`font-subheading text-xs sm:text-[13px] font-medium text-emerald-50 leading-normal ${
          item.clickable ? 'group-hover:text-amber-300 group-hover:underline' : ''
        }`}>
          {text}
        </span>

        {item.clickable && (
          <span className="text-[10px] text-amber-300 font-subheading font-bold">
            ↗
          </span>
        )}

        {/* Separator icon between ticker items */}
        <span className="text-emerald-600/80 mx-2 text-xs select-none">
          ✦
        </span>
      </div>
    );
  };

  return (
    <div className="ticker-container bg-gradient-to-r from-pansar-950 via-pansar-900 to-pansar-950 text-white border-b border-pansar-800/90 shadow-sm min-h-[40px] sm:min-h-[44px] flex items-center overflow-hidden relative">
      
      {/* Live Badge on right side */}
      <div className="z-10 bg-pansar-950/95 backdrop-blur-md px-3 sm:px-4 py-1.5 border-l border-pansar-800 flex items-center gap-1.5 shrink-0 shadow-md">
        <span className="w-2 h-2 rounded-full bg-emerald-400 animate-ping"></span>
        <span className="w-2 h-2 rounded-full bg-emerald-400 -mr-3.5"></span>
        <span className="font-subheading font-bold text-[11px] sm:text-xs text-amber-300 mr-1 tracking-wide">
          {lang === 'ur' ? 'تازہ اپڈیٹس' : 'Live Updates'}
        </span>
      </div>

      {/* Moving Marquee Track */}
      <div className="flex-1 overflow-hidden flex items-center">
        <div className="ticker-marquee-track">
          {/* First loop of items */}
          {tickerItems.map((item, idx) => renderItem(item, idx))}
          {/* Duplicate loop for seamless infinite smooth scrolling */}
          {tickerItems.map((item, idx) => renderItem(item, `dup-${idx}`))}
        </div>
      </div>

      {/* Desktop Only Actions: Phone, Email & Language Switcher (Visible on Computer / Laptops) */}
      <div className="hidden md:flex items-center gap-3 sm:gap-4 px-4 py-1 bg-pansar-950/95 backdrop-blur-md border-r border-pansar-800 shrink-0 z-10 font-subheading">
        <a
          href={`tel:${settings.phone}`}
          className="flex items-center gap-1.5 text-emerald-300 hover:text-white transition-colors"
          title="فون رابطہ"
        >
          <Phone className="w-3.5 h-3.5 text-emerald-400" />
          <span className="dir-ltr font-sans text-xs font-semibold">{settings.phone}</span>
        </a>

        <a
          href={`mailto:${settings.email || 'sherazi313@gmail.com'}`}
          className="hidden lg:flex items-center gap-1.5 text-emerald-300 hover:text-white transition-colors text-xs"
          title="ای میل"
        >
          <Mail className="w-3.5 h-3.5 text-emerald-400" />
          <span className="font-sans">{settings.email || 'sherazi313@gmail.com'}</span>
        </a>

        <div className="h-3.5 w-px bg-pansar-700"></div>

        <button
          onClick={toggleLanguage}
          className="flex items-center gap-1 px-2.5 py-0.5 rounded-full bg-pansar-800 hover:bg-pansar-700 text-emerald-200 hover:text-white transition border border-pansar-700 text-xs font-semibold cursor-pointer"
          title="تبدیل زبان / Switch Language"
        >
          <Globe className="w-3.5 h-3.5 text-emerald-400" />
          <span>{lang === 'ur' ? 'English' : 'اردو'}</span>
        </button>
      </div>

    </div>
  );
};

export default TopBanner;

